import React, { memo } from "react";
import { Handle, Position, NodeProps, useNodeConnections } from "@xyflow/react";
import {
  FlowEditorActionNode,
  useAgentWorkflowStore,
} from "@/store/agentsStore";
import { useAgentWorkflowActionsStore } from "@/store/agentWorkflowActionsStore";
import { NodeWrapper } from "./NodeWrapper";
import { NodeErrorDisplay } from "./NodeErrorDisplay";
import {
  Send,
  UserPlus,
  Settings,
  Mail,
  Database,
  Search,
  AlertCircle,
  Activity,
} from "lucide-react";

const actionIcons: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  SEND_MESSAGE: Send,
  ADD_CONTACT_TO_LIST: UserPlus,
  SELECT_ACTION: Settings,
  SEND_EMAIL: Mail,
  UPDATE_DATABASE: Database,
  SEARCH: Search,
  ALERT: AlertCircle,
  DEFAULT: Activity,
};

export const ActionNode = memo(
  ({ data, selected }: NodeProps<FlowEditorActionNode>) => {
    const nodeData = data.node;
    const workflowVersionId = data.workflowVersionId;

    const getActionById = useAgentWorkflowActionsStore(
      (state) => state.getActionById,
    );
    const action = nodeData.actionId
      ? getActionById(nodeData.actionId)
      : undefined;

    // Look up errors from the workflow version store
    const workflowVersion = useAgentWorkflowStore((state) =>
      workflowVersionId
        ? state.getWorkflowVersionById(workflowVersionId)
        : undefined,
    );
    const nodeErrors = workflowVersion?.errors?.[nodeData.id];

    const Icon = actionIcons[nodeData.actionId || ""] || actionIcons.DEFAULT;

    const connections = useNodeConnections({ handleType: "source" });

    return (
      <NodeWrapper
        selected={selected}
        nodeType="action"
        className="px-4 py-3 min-w-[220px]"
      >
        <Handle
          type="target"
          position={Position.Top}
          className="w-3 h-3 !bg-gray-400 !border-2 !border-white"
        />

        <NodeErrorDisplay errors={nodeErrors || []} />

        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Icon className="w-4 h-4 text-gray-700" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900">
              {action?.name || "Action"}
            </span>
          </div>
        </div>

        {nodeData.approvalConfig &&
          nodeData.approvalConfig.approvers.length > 0 && (
            <div className="mt-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3 text-orange-500" />
              <span className="text-xs text-orange-600">Requires approval</span>
            </div>
          )}

        <Handle
          type="source"
          position={Position.Bottom}
          // actions can only have one outgoing edge
          isConnectableStart={connections.length === 0}
          className="w-3 h-3 !bg-gray-400 !border-2 !border-white"
        />
      </NodeWrapper>
    );
  },
);

ActionNode.displayName = "ActionNode";
